import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { JobTypesEditor } from "@/components/JobTypesEditor";
import { useCompany } from "@/context/CompanyContext";
import { api } from "@/lib/api";
import { cleanTypes } from "@/lib/jobTypes";

// Save a draft list of job types for the whole company; returns false (after a toast) when it can't.
export const saveJobTypes = async (items, setCompany) => {
  const types = cleanTypes(items);
  if (!types.length) { toast.error("Add at least one job type"); return false; }
  try { setCompany(await api.setJobTypes(types)); return true; }
  catch (e) {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Could not save your job types");
    return false;
  }
};

export const JobTypesDialog = ({ open, onOpenChange }) => {
  const { company, setCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setItems(company?.job_types || []); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    if (await saveJobTypes(items, setCompany)) { toast.success("Job types saved"); onOpenChange(false); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="job-types-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Job types</DialogTitle>
          <DialogDescription>One list for your whole company, used when you create a job. Renames show on existing jobs; a change to how a type works applies to new jobs. Removing a type keeps it on jobs that already use it.</DialogDescription>
        </DialogHeader>
        <JobTypesEditor items={items} onChange={setItems} />
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-full" data-testid="job-types-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="job-types-save-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save job types
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
